import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useProfessional } from "../../contexts/ProfessionalContext";
import { Heading, Subheading } from "../../design/heading";
import { Link } from "../../design/link";
import LoadingView from "../../components/LoadingView";
import { Avatar } from "../../design/avatar";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useUser } from "../../contexts/UserContext";
import ProfessionalButton from "../../components/ProfessionalButton";
import ProfessionalRoleBadge from "../../components/ProfessionalRoleBadge";
import { Button } from "../../design/button";
import { Text } from "../../design/text";
import InviteProfessionalButton from "../../components/InviteProfessionalButton";

const ProfessionalDetailPage: React.FC = () => {
  const { currentUser } = useUser();
  const { professionalId } = useParams<{ professionalId: string }>();
  const {
    selectedProfessional: professional,
    setSelectedProfessional,
    error,
    loading,
  } = useProfessional();

  useEffect(() => {
    if (professionalId) {
      setSelectedProfessional(professionalId);
    }
  }, [professionalId]);

  if (loading.details) {
    return <LoadingView title="Loading Professional" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!professional) {
    return <ErrorView error={new Error("Professional not found")} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Professionals", href: "/professionals" },
          {
            label: professional.name,
            href: `/professional/${professional._id}`,
          },
        ]}
      />

      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {professional.avatarUrl && (
              <Avatar
                src={professional.avatarUrl}
                alt={professional.name}
                className="w-16 h-16 rounded-full"
              />
            )}
            <div>
              <Heading>{professional.name}</Heading>
            </div>
          </div>
          <div>
                      <InviteProfessionalButton professional={professional}>
            Invite
          </InviteProfessionalButton>
          {
            currentUser &&
            (["superadmin", "admin"].includes(currentUser.role) || currentUser.professional === professional._id) && (
            <ProfessionalButton professional={professional} />
          )}
          </div>
        </div>

        {/* About Section */}
        {professional.bio && (
          <div>
            <Subheading>About</Subheading>
            <Text>{professional.bio}</Text>
          </div>
        )}

        {/* Roles Section */}
        {professional.roles && professional.roles.length > 0 && (
          <div>
            <Subheading>Roles</Subheading>
            <div className="flex flex-wrap gap-2">
              {professional.roles.map((role) => (
                <ProfessionalRoleBadge key={role} role={role} />
              ))}
            </div>
          </div>
        )}

        {/* Other Details */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {professional.languagesSpoken &&
            professional.languagesSpoken.length > 0 && (
              <div>
                <Subheading>Languages Spoken</Subheading>
                <p className="text-gray-700 dark:text-gray-300">
                  {professional.languagesSpoken.join(", ")}
                </p>
              </div>
            )}
          {professional.hourlyRate && (
            <div>
              <Subheading>Hourly Rate</Subheading>
              <p className="text-gray-700 dark:text-gray-300">
                ${professional.hourlyRate}/hr
              </p>
            </div>
          )}
        </div>

        {/* Links Section */}
        <div className="flex flex-col gap-4">
          {professional.websiteUrl && (
            <Button
              href={professional.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Website
            </Button>
          )}
          {professional.schedulingUrl && (
            <Button
              href={professional.schedulingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule a Session
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDetailPage;
